import { useEffect, useState, type ReactNode } from 'react';
import { ProfileContext } from 'r/contexts';
import {
  useStateReducer,
  getIsSendTransactionErrorUnexpected,
  showNotification,
  captureException,
} from 'r/utils';
import { StoredAuth } from 'r/utils/Stored';
import {
  fetchApiRaw,
  GET_AUTH,
  POST_AUTH,
  GET_ISSUER,
  GET_AUTH_CMS,
  POST_AUTH_CMS,
} from 'r/utils/fetchApiRaw';
import { useAccount, useAccountEffect, useSignMessage } from 'wagmi';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ModalLoadingContent, SpinnerSan } from 'r/ui';
import { modal } from 'r/utils/wagmiConfig';
import ModalSignMessageContent from './ModalSignMessageContent';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useAppKit, useDisconnect } from '@reown/appkit/react';

type TokenType = 'default' | 'cms';
type AuthTokensData = Record<TokenType, string | null>;

const AUTH_CONFIGS = {
  default: {
    get: GET_AUTH,
    post: POST_AUTH,
  },
  cms: {
    get: GET_AUTH_CMS,
    post: POST_AUTH_CMS,
  },
  // play: {
  //   get: GET_AUTH_PLAY,
  //   post: POST_AUTH_PLAY,
  // },
};

class AuthTokens {
  static _getStorageData(): AuthTokensData {
    const stored = StoredAuth.getAll();

    if (typeof stored === 'string') {
      return { default: stored, cms: null };
    }

    return stored || { default: null, cms: null };
  }

  static setAll(tokens: AuthTokensData): void {
    StoredAuth.set(tokens);
  }

  static isFullyAuthenticated(): boolean {
    const tokens = this._getStorageData();
    return Boolean(tokens.default && tokens.cms);
  }

  static get(type: TokenType): string | null {
    const tokens = this._getStorageData();
    return tokens[type] || null;
  }

  static set(type: TokenType, token: string): void {
    const tokens = this._getStorageData();
    tokens[type] = token;
    this.setAll(tokens);
  }

  static clear(): void {
    StoredAuth.set(null);
  }
}

export default function ProfileProvider({ children }: { children: ReactNode }) {
  const [{ profile, isInitialProfileValueLoaded, isLoadingModalShown }, ss] =
    useStateReducer({
      profile: null,
      isInitialProfileValueLoaded: false,
      isLoadingModalShown: false,
    });

  const { disconnect } = useDisconnect();
  const navigate = useNavigate();
  const { open } = useAppKit();
  const account = useAccount();
  const { signMessageAsync } = useSignMessage();
  const [modalSignMessageConnectorName, setModalSignMessageConnectorName] =
    useState<null | string>(null);

  useEffect(() => {
    if (
      isInitialProfileValueLoaded &&
      profile &&
      account.isConnected &&
      profile.address !== account.address
    ) {
      disconnect();
    }
  }, [account.address, profile, isInitialProfileValueLoaded]);

  const logout = () => {
    ss({ profile: null });
    AuthTokens.clear();
    Sentry.setUser(null);
    navigate('/');
    if (account.isConnected) {
      disconnect();
    }
  };

  useEffect(() => {
    (async () => {
      try {
        if (!AuthTokens.isFullyAuthenticated()) {
          ss({ isInitialProfileValueLoaded: true });
          return;
        }

        const defaultToken = AuthTokens.get('default');
        const issuer = await fetchApiRaw(
          GET_ISSUER,
          {
            queryParams: { limit: 500 },
          },
          defaultToken,
        );

        issuer.accessToken = defaultToken;
        Sentry.setUser({ account: issuer.id });

        ss({ profile: issuer, isInitialProfileValueLoaded: true });
      } catch (e) {
        captureException(e);
        logout();
        ss({ isInitialProfileValueLoaded: true });
      }
    })();
  }, []);

  const authorizeService = async (
    type: TokenType,
    account: { address: string },
  ) => {
    const config = AUTH_CONFIGS[type];
    let getAuthResult;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        getAuthResult = await fetchApiRaw(config.get);
        break;
      } catch (e: unknown) {
        const status =
          (e as { response?: { status?: number }; extra?: { code?: number } })
            ?.response?.status ??
          (e as { extra?: { code?: number } })?.extra?.code;
        if (status !== undefined && status >= 500 && attempt < 2) {
          await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
          continue;
        }
        throw e;
      }
    }
    const { code: originalMessage } = getAuthResult;
    const signedMessage = await signMessageAsync({
      message: originalMessage,
      account: account.address as `0x${string}`,
    });
    const { accessToken } = await fetchApiRaw(config.post, {
      body: { originalMessage, signedMessage },
    });

    if (!accessToken) {
      throw new Error(`No access token received from ${type} auth`);
    }

    return accessToken;
  };

  const authorizeAll = async (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    account: any,
    cb = () => {},
  ) => {
    try {
      ss({ isLoadingModalShown: true });
      const { connector } = account;

      setModalSignMessageConnectorName(connector?.name ?? null);

      // const [defaultToken, cmsToken, playToken] = await Promise.all([
      const [defaultToken, cmsToken] = await Promise.all([
        authorizeService('default', account),
        authorizeService('cms', account),
        // authorizeService('play', account),
      ]);

      AuthTokens.setAll({
        default: defaultToken,
        cms: cmsToken,
        // play: playToken,
      });

      const issuer = await fetchApiRaw(
        GET_ISSUER,
        {
          queryParams: { limit: 500 },
        },
        defaultToken,
      );
      issuer.accessToken = defaultToken;

      Sentry.setUser({
        account: issuer.id,
        ethereumProviderName: connector.name,
      });

      ss({ profile: issuer });
      setModalSignMessageConnectorName(null);
      ss({ isLoadingModalShown: false });
      cb();
    } catch (e: unknown) {
      const status =
        (e as { response?: { status?: number }; extra?: { code?: number } })
          ?.response?.status ??
        (e as { extra?: { code?: number } })?.extra?.code;
      if (status !== undefined && status >= 500) {
        showNotification(
          'error',
          'Auth service is temporarily unavailable, please try again',
        );
      } else if (getIsSendTransactionErrorUnexpected(e)) {
        showNotification('error', 'Something went wrong, try again');
        captureException(e);
      }
      setModalSignMessageConnectorName(null);
      ss({ isLoadingModalShown: false });
      cb();
    }
  };

  useAccountEffect({
    onConnect(account) {
      // Skip re-authentication if tokens already exist in localStorage.
      // On page refresh wagmi auto-reconnects the wallet and fires onConnect,
      // but the init useEffect will restore the profile from stored tokens.
      if (!AuthTokens.isFullyAuthenticated()) {
        authorizeAll(account);
      }
    },
    onDisconnect() {
      logout();
    },
  });

  const auth = (cb = () => {}) => {
    if (account.isConnected) {
      authorizeAll(account, cb);
    } else {
      open({ view: 'Connect' });
      const unsubscribe = modal.subscribeEvents((event) => {
        if (['MODAL_CLOSE', 'CONNECT_SUCCESS'].includes(event.data.event)) {
          if (account.isConnected && event.data.event === 'CONNECT_SUCCESS') {
            authorizeAll(account, cb);
          } else {
            cb();
          }
          unsubscribe();
        }
      });
    }
  };

  const value = {
    profile,
    auth,
    logout,
    getToken: (type: TokenType = 'default') => AuthTokens.get(type),
    hasAllTokens: () => AuthTokens.isFullyAuthenticated(),
  };

  if (!isInitialProfileValueLoaded) {
    return (
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <SpinnerSan size={180} />
      </div>
    );
  }

  return (
    <ProfileContext.Provider value={value}>
      <Dialog
        open={!!modalSignMessageConnectorName}
        onOpenChange={(open) => !open && setModalSignMessageConnectorName(null)}
      >
        <DialogContent
          className="p-0 border-0 bg-transparent shadow-none min-w-xs"
          hideCloseButton
        >
          <ModalSignMessageContent
            connectorName={modalSignMessageConnectorName!}
          />
        </DialogContent>
      </Dialog>
      <Dialog
        open={isLoadingModalShown}
        onOpenChange={(open) => !open && ss({ isLoadingModalShown: false })}
      >
        <DialogContent
          className="p-0 border-0 bg-transparent shadow-none min-w-xs"
          hideCloseButton
        >
          <ModalLoadingContent />
        </DialogContent>
      </Dialog>
      {children}
    </ProfileContext.Provider>
  );
}
